/**
 * DigiLocker OAuth callback → complete the Sandbox session and show status.
 * Sandbox redirects the user here with no useful query params, so we retrieve
 * the session_id from sessionStorage (set by SandboxKycPanel before redirect).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Card } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";

const TERMINAL = new Set(["succeeded", "failed", "expired", "rejected"]);
const MAX_ATTEMPTS = 30;         // ~90s at 3s cadence
const MAX_CONSECUTIVE_ERRORS = 6; // ~18s of transient errors before we give up

export default function DigilockerCallback() {
  const nav = useNavigate();
  const [status, setStatus] = useState("checking");
  const [parsed, setParsed] = useState(null);
  const attemptsRef = useRef(0);
  const errorsRef = useRef(0);

  const poll = useCallback(async () => {
    const session_id = sessionStorage.getItem("venturaz_digilocker_session_id");
    if (!session_id) {
      setStatus("failed");
      toast.error("Session lost. Please restart DigiLocker verification.");
      return;
    }
    attemptsRef.current += 1;
    try {
      const { data } = await api.post("/kyc/sandbox/digilocker/complete", { session_id });
      errorsRef.current = 0; // reset transient error streak on any success
      const next = (data.status || "checking").toLowerCase();
      setStatus(next);
      if (data.parsed) setParsed(data.parsed);
      if (next === "succeeded") {
        sessionStorage.removeItem("venturaz_digilocker_session_id");
        toast.success("DigiLocker verification complete.");
      }
    } catch (err) {
      errorsRef.current += 1;
      // Only treat as failed after repeated errors OR max attempts
      if (
        errorsRef.current >= MAX_CONSECUTIVE_ERRORS ||
        attemptsRef.current >= MAX_ATTEMPTS
      ) {
        toast.error(formatApiError(err));
        setStatus("failed");
      }
      // otherwise stay in "checking" — interval will retry
    }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(() => {
      if (TERMINAL.has(status) || attemptsRef.current >= MAX_ATTEMPTS) {
        clearInterval(t);
        return;
      }
      poll();
    }, 3000);
    return () => clearInterval(t);
  }, [poll, status]);

  const returnHref = () => {
    try {
      const role = JSON.parse(localStorage.getItem("venturaz_user") || "{}").role;
      if (role === "investor") return "/investor/kyc";
      return "/startup/kyc";
    } catch {
      return "/startup/kyc";
    }
  };

  const done = TERMINAL.has(status);
  const succeeded = status === "succeeded";

  return (
    <DashboardShell title="DigiLocker Verification" subtitle="RETURNING FROM DIGILOCKER">
      <Toaster richColors position="top-right" />
      <Card testId="digilocker-callback" className="max-w-xl">
        <div className="flex items-center gap-3 mb-3">
          {done ? (
            succeeded ? <CheckCircle2 className="text-emerald-600" /> : <XCircle className="text-red-600" />
          ) : (
            <Loader2 className="animate-spin text-gold" />
          )}
          <div className="font-heading text-ink text-lg" data-testid="digilocker-callback-heading">
            {succeeded ? "Verified" : done ? `Session ${status}` : "Confirming with DigiLocker…"}
          </div>
        </div>
        {succeeded && parsed ? (
          <div className="text-sm space-y-1 text-ink-soft" data-testid="digilocker-callback-parsed">
            {parsed.name && <div><span className="text-ink-muted">Name:</span> {parsed.name}</div>}
            {parsed.dob && <div><span className="text-ink-muted">Date of Birth:</span> {parsed.dob}</div>}
            {parsed.gender && <div><span className="text-ink-muted">Gender:</span> {parsed.gender}</div>}
            {parsed.address && <div><span className="text-ink-muted">Address:</span> {parsed.address}</div>}
          </div>
        ) : (
          <p className="text-sm text-ink-soft">
            We are fetching your KYC payload from Sandbox. This usually takes 5–10 seconds.
            If nothing happens for a minute, please return to KYC and try again.
          </p>
        )}
        <div className="mt-5">
          <Button onClick={() => nav(returnHref())} data-testid="digilocker-callback-return">
            Return to KYC
          </Button>
        </div>
      </Card>
    </DashboardShell>
  );
}
